import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X } from "lucide-react";
import { referenceDataAPI, tagsAPI } from "../services/api";
import TemplateConfirmationModal from "../Modal/TemplateConfirmationModal";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 500px;
  height: 680px;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #E5E7EB;
  position: relative;
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  flex: 1;
  text-align: left;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9CA3AF;
  padding: 0.5rem;
  border-radius: 0.25rem;
  position: absolute;
  right: 0;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
`;

const ContentSection = styled.div`
  padding: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TagContainer = styled.div`
  position: relative;
`;

const TagInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  position: relative;
`;

const TagInput = styled.input`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const TagSuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 70px;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  margin-top: 4px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 10;
`;

const TagSuggestionItem = styled.div`
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #F3F4F6;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #E5E7EB;
  }
`;

const AddTagButton = styled.button`
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  color: #374151;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: 1px solid #00B5B8;
  color: #00B5B8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
`;

const TagRemoveButton = styled.button`
  background: white;
  border: 1px solid #00B5B8;
  cursor: pointer;
  color: #00B5B8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const GenderSection = styled.div`
  margin-bottom: 1rem;
`;

const GenderAgeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 0.5rem;
`;

const GenderColumn = styled.div`
  flex: 1;
`;

const AgeColumn = styled.div`
  flex: 1;
`;

const GenderOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.4rem;
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const RadioInput = styled.input`
  width: 0.9rem;
  height: 0.9rem;
  accent-color: #1D87F0;

  &:checked {
    background-color: #1D87F0;
    border-color: #1D87F0;
  }
`;

const RadioLabel = styled.label`
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
`;

const AgeRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.4rem;
`;

const DualRangeContainer = styled.div`
  width: 140px;
  position: relative;
  height: 4px;
`;

const RangeTrack = styled.div`
  width: 100%;
  height: 4px;
  background: #E5E7EB;
  border-radius: 2px;
  position: relative;
  overflow: visible;
`;

const RangeProgress = styled.div`
  height: 100%;
  background: #1D87F0;
  border-radius: 2px;
  position: absolute;
  left: calc(${props => props.left}% + ${props => (props.left / 100) * 7}px);
  right: calc(${props => (100 - props.left - props.width)}% + ${props => ((100 - props.left - props.width) / 100) * 7}px);
  pointer-events: none;
`;

const RangeInput = styled.input`
  position: absolute;
  width: 100%;
  height: 4px;
  background: transparent;
  pointer-events: none;
  appearance: none;
  outline: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    pointer-events: all;
    position: relative;
    z-index: 2;
    margin-top: -5px;
    transform: translateY(-0.5px);
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    border: none;
    pointer-events: all;
    margin-top: -5px;
  }
`;

const AgeDisplay = styled.div`
  background-color: #1D87F0;
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  min-width: 60px;
  text-align: center;
`;

const MultiSelectContainer = styled.div`
  position: relative;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
`;

const MultiSelectWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const MultiSelectButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.8rem;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  height: fit-content;

  &:hover {
    background-color: #0891B2;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-bottom: 1px solid #E5E7EB;
  font-size: 0.8rem;

  &:focus {
    outline: none;
  }
`;

const ParameterOption = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const Checkbox = styled.input`
  width: 0.8rem;
  height: 0.8rem;
  accent-color: #00B5B8;
`;

const TableContainer = styled.div`
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F9FAFB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.5rem;
  text-align: left;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
`;

const TableCell = styled.td`
  padding: 0.5rem;
  font-size: 0.8rem;
  color: #374151;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;
`;

const TableDualRangeContainer = styled.div`
  flex: 1;
  position: relative;
  height: 3px;
  min-width: 150px;
`;

const TableRangeTrack = styled.div`
  width: 100%;
  height: 3px;
  background: #E5E7EB;
  border-radius: 1.5px;
  position: relative;
  overflow: hidden;
`;

const TableRangeProgress = styled.div`
  height: 100%;
  background: #1D87F0;
  border-radius: 1.5px;
  position: absolute;
  left: ${props => props.left}%;
  width: ${props => props.width}%;
  z-index: 1;
  pointer-events: none;
`;

const TableRangeInput = styled.input`
  position: absolute;
  width: 100%;
  height: 3px;
  background: transparent;
  pointer-events: none;
  appearance: none;
  outline: none;
  z-index: 3;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    pointer-events: all;
    position: relative;
    margin-top: -3.5px;
    transform: translateY(-0.5px);
  }

  &::-moz-range-thumb {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #1D87F0;
    cursor: pointer;
    border: none;
    pointer-events: all;
    margin-top: -3.5px;
  }
`;

const TableRangeDisplay = styled.div`
  font-size: 0.75rem;
  color: #374151;
  min-width: 70px;
  text-align: right;
  font-weight: 600;
  white-space: nowrap;
`;

const NormalRangeBadge = styled.span`
  background: transparent;
  color: #374151;
  padding: 0;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: normal;
`;

const RadioOptionsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #E5E7EB;
`;

const CancelButton = styled.button`
  background-color: white;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #F9FAFB;
  }
`;

const SaveButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  cursor: pointer;

  &:hover {
    background-color: #0891B2;
  }
`;

const CreateTemplateModal = ({ isOpen, onClose, onSave }) => {
  const [templateName, setTemplateName] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]); // tags ที่ผู้ใช้เลือกแล้ว
  const [availableTags, setAvailableTags] = useState([]); // tags ทั้งหมดที่มีในระบบ
  const [showTagSuggestions, setShowTagSuggestions] = useState(false); // แสดง/ซ่อน autocomplete
  const [selectedGender, setSelectedGender] = useState("male");
  const [ageRange, setAgeRange] = useState([25, 65]);
  const [selectedParameters, setSelectedParameters] = useState([]);
  const [tempSelectedParameters, setTempSelectedParameters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [parameterRanges, setParameterRanges] = useState({});
  const [parameterRadioValues, setParameterRadioValues] = useState({}); // สำหรับเก็บค่า radio (ไม่มี/มี)
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [allParameters, setAllParameters] = useState([]);
  const [loading, setLoading] = useState(false);

  // Reset state เมื่อเปิด modal
  useEffect(() => {
    if (isOpen) {
      setTags([]); // clear tags ที่เลือก
      setTagInput(""); // clear input
      setShowTagSuggestions(false); // ซ่อน suggestions
    }
  }, [isOpen]);

  // ดึงข้อมูล tags ทั้งหมดจากฐานข้อมูล (template_tags) เก็บไว้ใน availableTags
  useEffect(() => {
    if (!isOpen) return;

    const loadTags = async () => {
      try {
        const response = await tagsAPI.getUsedTags();

        if (response.status === 'success' && response.data && Array.isArray(response.data)) {
          // แปลงเป็น array ของ tag names
          const tagNames = response.data.map(tag => tag.tag_name);
          setAvailableTags(tagNames); // เก็บไว้ใน availableTags แทน tags
          console.log('✅ โหลด tags สำเร็จ:', tagNames);
        }
      } catch (error) {
        console.error('✗ Error loading tags:', error);
        // ถ้า error ใช้ mock data
        setAvailableTags(["ผู้สูงอายุ", "พันธุกรรม", "เบาหวาน", "ความดันโลหิตสูง"]);
      }
    };

    loadTags();
  }, [isOpen]);

  // ดึงข้อมูลพารามิเตอร์จาก API เมื่อเพศหรืออายุเปลี่ยน
  useEffect(() => {
    if (!isOpen) return;

    const loadParameters = async () => {
      setLoading(true);
      try {
        // แปลงเพศจาก 'male'/'female'/'both' เป็น 'M'/'F'/null
        let gender = null;
        if (selectedGender === 'male') gender = 'M';
        else if (selectedGender === 'female') gender = 'F';
        // both = null (ดึงทั้งหมด)

        // ใช้อายุกลางของ range
        const avgAge = (ageRange[0] + ageRange[1]) / 2;

        const response = await referenceDataAPI.getParametersList({
          gender: gender,
          age: avgAge
        });

        // API Backend ส่งกลับมาในรูปแบบ { status: "success", message: "...", data: [...] }
        if (response.status === 'success' && response.data && Array.isArray(response.data)) {
          // API ส่งกลับมาเป็น array ของ objects โดยตรง
          const parameters = response.data.map(paramData => {
            // normalRange มาจาก API แล้ว (เป็น array [min, max])
            const normalRange = paramData.normalRange || [0, 100];

            return {
              name: paramData.name,
              unit: paramData.unit || '',
              normalRange: normalRange,
              normalValue: paramData.normalValue,
              category: paramData.category
            };
          });

          setAllParameters(parameters);
          console.log('✅ โหลดพารามิเตอร์สำเร็จ:', parameters.length, 'รายการ');
        } else {
          console.warn('⚠ API Response:', response);
          setAllParameters([]);
        }
      } catch (error) {
        console.error('✗ Error loading parameters:', error);

        // ใช้ข้อมูล mock data เพื่อทดสอบ UI
        const mockParameters = [
          { name: 'Albumin', unit: 'g/dL', normalRange: [3.8, 5.4], normalValue: '3.8-5.4', category: 'การทำงานของตับ' },
          { name: 'WBC', unit: 'x10³/µL', normalRange: [4.5, 13.5], normalValue: '4.5-13.5', category: 'เม็ดเลือดขาว' },
          { name: 'Creatinine', unit: 'mg/dL', normalRange: [0.55, 1.02], normalValue: '0.55-1.02', category: 'การทำงานของไต' },
          { name: 'HbA1C', unit: '%', normalRange: [0, 5.7], normalValue: '<5.7', category: 'น้ำตาลเฉลี่ยสะสม' },
        ];

        console.warn('⚠ ใช้ Mock Data แทน (', mockParameters.length, 'รายการ)');
        setAllParameters(mockParameters);
      } finally {
        setLoading(false);
      }
    };

    loadParameters();
  }, [selectedGender, ageRange, isOpen]);

  const addTag = (tagName = null) => {
    const tagToAdd = tagName || tagInput.trim();
    if (tagToAdd && !tags.includes(tagToAdd)) {
      setTags([...tags, tagToAdd]);
      setTagInput("");
      setShowTagSuggestions(false);
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // กรอง tags ที่ตรงกับคำค้นหา และยังไม่ได้เลือก
  const filteredTags = availableTags.filter(tag =>
    tag.toLowerCase().includes(tagInput.toLowerCase()) &&
    !tags.includes(tag)
  );

  // แสดง autocomplete เมื่อมีการพิมพ์
  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setTagInput(value);
    setShowTagSuggestions(value.trim().length > 0);
  };

  const handleMinAgeChange = (e) => {
    const value = parseInt(e.target.value);
    setAgeRange([value, Math.max(value, ageRange[1])]);
  };

  const handleMaxAgeChange = (e) => {
    const value = parseInt(e.target.value);
    setAgeRange([Math.min(ageRange[0], value), value]);
  };

  const getProgressLeft = () => (ageRange[0] / 100) * 100;
  const getProgressWidth = () => ((ageRange[1] - ageRange[0]) / 100) * 100;

  const filteredParameters = allParameters.filter(param =>
    param.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleParameterToggle = (parameter) => {
    const isSelected = tempSelectedParameters.some(p => p.name === parameter.name);
    if (isSelected) {
      setTempSelectedParameters(tempSelectedParameters.filter(p => p.name !== parameter.name));
    } else {
      setTempSelectedParameters([...tempSelectedParameters, parameter]);
    }
  };

  // ฟังก์ชันตรวจสอบว่าพารามิเตอร์เป็นชนิด radio (ไม่มี/มี) หรือไม่
  const isRadioParameter = (parameter) => {
    const radioParams = [
      'Protein', 'Glucose', 'Ketones', 'Blood', 'Leukocytes', 'Nitrite', 'Bilirubin', 'Urobilinogen',
      'Color', 'Clarity',
      'Color (Stool)', 'Appearance (Stool)', 'WBC (Stool)', 'RBC (Stool)', 'Parasite',
      'RBC Index', 'eGFR'
    ];
    return radioParams.includes(parameter.name);
  };

  // ฟังก์ชันเพื่อดึงตัวเลือกสำหรับ radio button แต่ละพารามิเตอร์
  const getRadioOptions = (parameterName) => {
    const optionsMap = {
      'Protein': ['ไม่มี', 'มี'],
      'Glucose': ['ไม่มี', 'มี'],
      'Ketones': ['ไม่มี', 'มี'],
      'Blood': ['ไม่มี', 'มี'],
      'Leukocytes': ['ไม่มี', 'มี'],
      'Nitrite': ['ไม่มี', 'มี'],
      'Bilirubin': ['ไม่มี', 'มี'],
      'Urobilinogen': ['ไม่มี', 'มี'],
      'Color': ['เหลืองอ่อน', 'ผิดปกติ'],
      'Clarity': ['Clear', 'ผิดปกติ'],
      'Color (Stool)': ['xxx'],
      'Appearance (Stool)': ['xxx'],
      'WBC (Stool)': ['Not found', 'Found'],
      'RBC (Stool)': ['Not found', 'Found'],
      'Parasite': ['Not found', 'Found'],
      'RBC Index': ['xxx'],
      'eGFR': ['xxx']
    };
    return optionsMap[parameterName] || ['ไม่มี', 'มี'];
  };

  const handleSelectParameters = () => {
    setSelectedParameters([...tempSelectedParameters]);

    const newRanges = { ...parameterRanges };
    const newRadioValues = { ...parameterRadioValues };

    Object.keys(newRanges).forEach(paramName => {
      if (!tempSelectedParameters.some(p => p.name === paramName)) {
        delete newRanges[paramName];
      }
    });

    Object.keys(newRadioValues).forEach(paramName => {
      if (!tempSelectedParameters.some(p => p.name === paramName)) {
        delete newRadioValues[paramName];
      }
    });

    tempSelectedParameters.forEach(parameter => {
      if (isRadioParameter(parameter)) {
        // ถ้าเป็น radio parameter ให้ตั้งค่าเริ่มต้นเป็นตัวเลือกแรก
        if (!newRadioValues[parameter.name]) {
          const options = getRadioOptions(parameter.name);
          newRadioValues[parameter.name] = options[0];
        }
      } else {
        // ถ้าเป็น range parameter ให้ใช้ normalRange เป็นค่าเริ่มต้น
        if (!newRanges[parameter.name]) {
          newRanges[parameter.name] = parameter.normalRange;
        }
      }
    });

    setParameterRanges(newRanges);
    setParameterRadioValues(newRadioValues);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handleDropdownOpen = () => {
    setTempSelectedParameters([...selectedParameters]);
    setIsDropdownOpen(true);
  };

  // คำนวณค่ากลางจาก normalValue string
  const calculateMiddleValue = (normalValue) => {
    if (!normalValue) return 50; // default

    const trimmed = normalValue.trim();

    // กรณี >50 หรือ <130
    if (trimmed.startsWith('>') || trimmed.startsWith('<')) {
      return parseFloat(trimmed.substring(1));
    }

    // กรณี 3.5-7.2
    if (trimmed.includes('-')) {
      const parts = trimmed.split('-').map(p => parseFloat(p));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return (parts[0] + parts[1]) / 2;
      }
    }

    // กรณีเป็นตัวเลขเดียว เช่น 9, 12
    const singleNum = parseFloat(trimmed);
    if (!isNaN(singleNum)) {
      return singleNum;
    }

    return 50; // default
  };

  // คำนวณช่วงที่สามารถเลื่อนได้ ±60% จากค่ากลาง
  const getAdjustableRange = (normalRange, normalValue = null) => {
    let middle;

    // ถ้ามี normalValue (string) ให้คำนวณจากนั้น
    if (normalValue) {
      middle = calculateMiddleValue(normalValue);
    } else {
      // ถ้าไม่มี ใช้ normalRange แบบเดิม
      const [normalMin, normalMax] = normalRange;
      middle = (normalMin + normalMax) / 2;
    }

    // คำนวณช่วง ±60% จากค่ากลาง
    const rangeSpan = middle * 0.6;
    const adjustableMin = Math.max(0, middle - rangeSpan); // ไม่ให้ติดลบ
    const adjustableMax = middle + rangeSpan;

    return [adjustableMin, adjustableMax];
  };

  const handleParameterRangeChange = (paramName, isMin, value) => {
    const currentRange = parameterRanges[paramName] || [0, 100];
    const numValue = parseFloat(value);
    const newRange = isMin
      ? [numValue, Math.max(numValue, currentRange[1])]
      : [Math.min(currentRange[0], numValue), numValue];

    setParameterRanges({
      ...parameterRanges,
      [paramName]: newRange
    });
  };

  const handleRadioChange = (paramName, value) => {
    setParameterRadioValues({
      ...parameterRadioValues,
      [paramName]: value
    });
  };

  // คำนวณตำแหน่งของ slider progress bar
  // ใช้ adjustableRange เพื่อให้ตรงกับ slider ที่ใช้ adjustableRange
  const getTableProgressLeft = (param) => {
    const range = parameterRanges[param.name] || param.normalRange;
    const adjustableRange = getAdjustableRange(param.normalRange, param.normalValue);
    const [minVal, maxVal] = adjustableRange;
    const totalRange = maxVal - minVal;

    const left = ((range[0] - minVal) / totalRange) * 100;
    return Math.max(0, Math.min(100, left)); // จำกัดไว้ 0-100%
  };

  const getTableProgressWidth = (param) => {
    const range = parameterRanges[param.name] || param.normalRange;
    const adjustableRange = getAdjustableRange(param.normalRange, param.normalValue);
    const [minVal, maxVal] = adjustableRange;
    const totalRange = maxVal - minVal;

    const left = ((range[0] - minVal) / totalRange) * 100;
    const right = ((range[1] - minVal) / totalRange) * 100;

    // จำกัด left และ right ให้อยู่ใน 0-100%
    const clampedLeft = Math.max(0, Math.min(100, left));
    const clampedRight = Math.max(0, Math.min(100, right));

    return clampedRight - clampedLeft;
  };

  const handleSave = () => {
    setShowConfirmation(true);
  };

  const handleConfirmSave = () => {
    const newTemplate = {
      templateName,
      createdBy,
      tags,
      gender: selectedGender,
      ageRange,
      selectedParameters,
      parameterRanges,
      parameterRadioValues
    };
    onSave(newTemplate);
    setShowConfirmation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>สร้าง Template</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ContentSection>
            <FormGroup>
              <Label>ชื่อ Template</Label>
              <Input
                type="text"
                placeholder="ชื่อ Template"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ชื่อผู้สร้าง</Label>
              <Input
                type="text"
                placeholder="ชื่อผู้สร้าง"
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>ป้ายกำกับ (Tag)</Label>
              <TagContainer>
                <TagInputContainer>
                  <TagInput
                    type="text"
                    placeholder="พิมพ์เพื่อค้นหา tag..."
                    value={tagInput}
                    onChange={handleTagInputChange}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    onFocus={() => setShowTagSuggestions(tagInput.trim().length > 0)}
                    onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                  />
                  <AddTagButton onClick={() => addTag()}>เพิ่ม</AddTagButton>

                  {showTagSuggestions && filteredTags.length > 0 && (
                    <TagSuggestionsDropdown>
                      {filteredTags.map((tag, index) => (
                        <TagSuggestionItem
                          key={index}
                          onClick={() => addTag(tag)}
                        >
                          {tag}
                        </TagSuggestionItem>
                      ))}
                    </TagSuggestionsDropdown>
                  )}
                </TagInputContainer>

                {tags.length > 0 && (
                  <TagList>
                    {tags.map((tag, index) => (
                      <Tag key={index}>
                        <span>{tag}</span>
                        <TagRemoveButton onClick={() => removeTag(tag)}>
                          <X size={8} />
                        </TagRemoveButton>
                      </Tag>
                    ))}
                  </TagList>
                )}
              </TagContainer>
            </FormGroup>

            <GenderSection>
              <GenderAgeRow>
                <GenderColumn>
                  <Label>สำหรับเพศ</Label>
                  <GenderOptions>
                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={selectedGender === "male"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="male">ชาย</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={selectedGender === "female"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="female">หญิง</RadioLabel>
                    </RadioContainer>

                    <RadioContainer>
                      <RadioInput
                        type="radio"
                        id="both"
                        name="gender"
                        value="both"
                        checked={selectedGender === "both"}
                        onChange={(e) => setSelectedGender(e.target.value)}
                      />
                      <RadioLabel htmlFor="both">ทุกเพศ</RadioLabel>
                    </RadioContainer>
                  </GenderOptions>
                </GenderColumn>

                <AgeColumn>
                  <Label>ช่วงอายุ (ปี)</Label>
                  <AgeRangeContainer>
                    <DualRangeContainer>
                      <RangeTrack>
                        <RangeProgress
                          left={getProgressLeft()}
                          width={getProgressWidth()}
                        />
                      </RangeTrack>
                      <RangeInput
                        type="range"
                        min="0"
                        max="100"
                        value={ageRange[0]}
                        onChange={handleMinAgeChange}
                      />
                      <RangeInput
                        type="range"
                        min="0"
                        max="100"
                        value={ageRange[1]}
                        onChange={handleMaxAgeChange}
                      />
                    </DualRangeContainer>
                    <AgeDisplay>{ageRange[0]}-{ageRange[1]}</AgeDisplay>
                  </AgeRangeContainer>
                </AgeColumn>
              </GenderAgeRow>
            </GenderSection>

            <FormGroup>
              <Label>เลือกพาราเมิเตอร์</Label>
              <MultiSelectContainer>
                <MultiSelectWrapper>
                  <MultiSelectButton
                    type="button"
                    onClick={handleDropdownOpen}
                  >
                    <span>
                      {selectedParameters.length === 0
                        ? 'เลือกพาราเมิเตอร์'
                        : `${selectedParameters.length} selected`}
                    </span>
                    <span>{isDropdownOpen ? '▲' : '▼'}</span>
                  </MultiSelectButton>

                  {isDropdownOpen && (
                    <DropdownContent>
                      <SearchInput
                        type="text"
                        placeholder="ค้นหาพาราเมิเตอร์..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {loading ? (
                        <ParameterOption style={{ justifyContent: 'center', color: '#6B7280' }}>
                          กำลังโหลดพารามิเตอร์...
                        </ParameterOption>
                      ) : filteredParameters.length === 0 ? (
                        <ParameterOption style={{ justifyContent: 'center', color: '#6B7280' }}>
                          {searchTerm ? 'ไม่พบพารามิเตอร์ที่ค้นหา' : 'ไม่มีพารามิเตอร์'}
                        </ParameterOption>
                      ) : (
                        filteredParameters.map((parameter, index) => (
                          <ParameterOption key={index} onClick={() => handleParameterToggle(parameter)}>
                            <Checkbox
                              type="checkbox"
                              checked={tempSelectedParameters.some(p => p.name === parameter.name)}
                              onChange={() => {}}
                            />
                            <span>{parameter.name} ({parameter.unit})</span>
                          </ParameterOption>
                        ))
                      )}
                    </DropdownContent>
                  )}
                </MultiSelectWrapper>

                <SelectButton onClick={handleSelectParameters}>
                  เลือก
                </SelectButton>
              </MultiSelectContainer>
            </FormGroup>

            <TableContainer>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>พาราเมิเตอร์</TableHeaderCell>
                    <TableHeaderCell>ค่าปกติ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่กำหนด</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <tbody>
                  {selectedParameters.length > 0 ? (
                    selectedParameters.map((param, index) => {
                      const isRadio = isRadioParameter(param);

                      if (isRadio) {
                        // แสดง radio button สำหรับพารามิเตอร์ที่เป็น text-based
                        const options = getRadioOptions(param.name);
                        const radioValue = parameterRadioValues[param.name] || options[0];

                        return (
                          <TableRow key={index}>
                            <TableCell>{param.name}</TableCell>
                            <TableCell>
                              <NormalRangeBadge>{param.normalValue}</NormalRangeBadge>
                            </TableCell>
                            <TableCell>
                              <RadioOptionsContainer>
                                {options.map((option, optionIndex) => (
                                  <RadioContainer key={optionIndex}>
                                    <RadioInput
                                      type="radio"
                                      id={`${param.name}-${optionIndex}`}
                                      name={param.name}
                                      value={option}
                                      checked={radioValue === option}
                                      onChange={(e) => handleRadioChange(param.name, e.target.value)}
                                    />
                                    <RadioLabel htmlFor={`${param.name}-${optionIndex}`}>{option}</RadioLabel>
                                  </RadioContainer>
                                ))}
                              </RadioOptionsContainer>
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        // แสดง range slider สำหรับพารามิเตอร์ปกติ
                        const range = parameterRanges[param.name] || param.normalRange;

                        // คำนวณช่วงที่ปรับได้ ±60% จากค่ากลาง
                        const adjustableRange = getAdjustableRange(param.normalRange, param.normalValue);
                        const [minVal, maxVal] = adjustableRange;

                        // กำหนด step ให้ละเอียดขึ้นสำหรับการเลื่อนที่ smooth
                        const rangeSpan = maxVal - minVal;
                        const step = rangeSpan < 1 ? 0.01 : (rangeSpan < 10 ? 0.1 : 0.5);

                        return (
                          <TableRow key={index}>
                            <TableCell>{param.name}</TableCell>
                            <TableCell>
                              <NormalRangeBadge>{param.normalValue}</NormalRangeBadge>
                            </TableCell>
                            <TableCell>
                              <SliderRow>
                                <TableDualRangeContainer>
                                  <TableRangeTrack>
                                    <TableRangeProgress
                                      left={getTableProgressLeft(param)}
                                      width={getTableProgressWidth(param)}
                                    />
                                  </TableRangeTrack>
                                  <TableRangeInput
                                    type="range"
                                    min={minVal}
                                    max={maxVal}
                                    step={step}
                                    value={range[0]}
                                    onChange={(e) => handleParameterRangeChange(param.name, true, e.target.value)}
                                  />
                                  <TableRangeInput
                                    type="range"
                                    min={minVal}
                                    max={maxVal}
                                    step={step}
                                    value={range[1]}
                                    onChange={(e) => handleParameterRangeChange(param.name, false, e.target.value)}
                                  />
                                </TableDualRangeContainer>
                                <TableRangeDisplay>
                                  {parseFloat(range[0]).toFixed(step < 1 ? 2 : (step < 1 ? 1 : 0))}-{parseFloat(range[1]).toFixed(step < 1 ? 2 : (step < 1 ? 1 : 0))}
                                </TableRangeDisplay>
                              </SliderRow>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    })
                  ) : (
                    <TableRow>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
          </ContentSection>

          <ActionSection>
            <CancelButton onClick={onClose}>ยกเลิก</CancelButton>
            <SaveButton onClick={handleSave}>สร้าง Template</SaveButton>
          </ActionSection>
        </ModalBody>
      </ModalContainer>

      <TemplateConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmSave}
      />
    </ModalOverlay>
  );
};

export default CreateTemplateModal;
